// src/pages/UtilityAllowanceJoinPaymentPage.js
// Payment step after the join form. The join page redirects here once the
// request is saved, with ?member=yes|no, which selects the annual rate:
//   member=yes → $300 (Section 8 Administrators member rate)
//   otherwise  → $350
// Route: /utility-allowances/join/payment

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Brand from '../components/Brand';
import { PAYMENT_LINKS, PRICES } from '../config/uaPayment';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const T = {
  blue600: '#1d4ed8', // blue-700: reads at 4.5:1 on the blue-100 chips (WCAG 1.4.3),
  blue50: '#eff6ff',
  blue100: '#dbeafe',
  gray900: '#111827',
  gray800: '#1f2937',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// Same compliance-service base the join form saves to.
const COMPLIANCE_API =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

const altInputStyle = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  fontSize: '0.9rem',
  border: `1px solid ${T.gray200}`,
  borderRadius: '12px',
  outline: 'none',
  fontFamily: T.font,
  color: T.gray900,
  background: T.white,
  boxSizing: 'border-box',
};

export default function UtilityAllowanceJoinPaymentPage() {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const isMember = searchParams.get('member') === 'yes';
  const amount = isMember ? PRICES.member : PRICES.standard;

  // Alternative payment request (check, purchase order, ...) for authorities
  // that cannot use the checkout payment methods. Prefilled from the join
  // submission: the link's jid/email win when present (a payment link sent by
  // email, opened on any machine), else whatever the form page stored in this
  // browser's localStorage.
  const joinInfo = (() => {
    let stored = {};
    try { stored = JSON.parse(localStorage.getItem('ua_join_info') || 'null') || {}; } catch {}
    const jid = searchParams.get('jid');
    const email = searchParams.get('email');
    if (jid || email) {
      return {
        id: jid ? Number(jid) || null : null,
        email: email || '',
        housingAuthority: jid && stored.id && String(stored.id) === jid ? (stored.housingAuthority || '') : '',
      };
    }
    return stored;
  })();
  // Base Stripe Payment Link for this rate.
  const basePayUrl = isMember ? PAYMENT_LINKS.member : PAYMENT_LINKS.standard;
  // Tie the Stripe payment back to the exact join-request row. client_reference_id
  // is stamped onto the resulting checkout session and subscription, so any payment
  // can be traced to its ua_join_requests id. prefilled_email nudges the buyer to
  // pay with the same address they used on the form.
  const payUrl = (() => {
    if (!basePayUrl) return '';
    const params = new URLSearchParams();
    if (joinInfo.id) params.set('client_reference_id', `join-${joinInfo.id}`);
    if (joinInfo.email) params.set('prefilled_email', joinInfo.email);
    const qs = params.toString();
    return qs ? `${basePayUrl}?${qs}` : basePayUrl;
  })();
  const [showAlt, setShowAlt] = useState(false);
  const [altHa, setAltHa] = useState(joinInfo.housingAuthority || '');
  const [altEmail, setAltEmail] = useState(joinInfo.email || '');
  const [altMethod, setAltMethod] = useState('');
  const [altSending, setAltSending] = useState(false);
  const [altSent, setAltSent] = useState(false);
  const [altError, setAltError] = useState('');

  const submitAlt = async () => {
    if (!altHa.trim() || !altEmail.trim() || !altMethod.trim() || altSending) {
      setAltError('Please fill in all three fields.');
      return;
    }
    setAltSending(true); setAltError('');
    try {
      const res = await fetch(`${COMPLIANCE_API}/api/utility-allowance/payment-method-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          joinRequestId: joinInfo.id || null,
          housingAuthority: altHa,
          email: altEmail,
          method: altMethod,
        }),
      });
      if (!res.ok) throw new Error(`save ${res.status}`);
      setAltSent(true);
    } catch (err) {
      setAltError('Something went wrong saving your request. Please try again, or email contact@gethousingai.com.');
    } finally {
      setAltSending(false);
    }
  };

  return (
    <div style={{
      fontFamily: T.font,
      minHeight: '100vh',
      background: T.gradient,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <Brand as="a" href="https://gethousingai.com" target="_blank" rel="noopener noreferrer" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', fontFamily: T.font, textDecoration: 'none' }} />
          <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
          <span style={{
            fontSize: mobile ? '0.72rem' : '0.85rem',
            padding: mobile ? '0.2rem 0.55rem' : '0.25rem 0.75rem',
            background: T.blue100,
            color: T.blue600,
            borderRadius: T.radiusPill,
            fontWeight: 600,
          }}>
            Payment · Utility Allowance Schedule
          </span>
        </div>
      </header>

      {/* Main */}
      <main style={{ padding: mobile ? '1.5rem 0.75rem' : '3rem 1.5rem', flex: 1 }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          {/* Request received */}
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: T.radiusSm,
            padding: '0.9rem 1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            marginBottom: '1.25rem',
          }}>
            <span style={{ color: '#15803d', fontSize: '1.1rem', fontWeight: 700 }}>✓</span>
            <span style={{ color: '#166534', fontSize: '0.88rem' }}>
              Your information was received. One step left: payment.
            </span>
          </div>

          {/* Payment card */}
          <div style={{
            background: T.white,
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radius,
            boxShadow: T.shadow,
            padding: mobile ? '1.5rem 1.25rem' : '2rem',
            textAlign: 'center',
          }}>
            <h1 style={{
              fontSize: mobile ? '1.2rem' : '1.4rem',
              fontWeight: 800,
              color: T.gray900,
              margin: '0 0 0.35rem',
              letterSpacing: '-0.02em',
            }}>
              Utility Allowance Schedule
            </h1>
            <p style={{ color: T.gray600, fontSize: '0.85rem', margin: '0 0 1.25rem' }}>
              Annual subscription · HUSM-computed, fully cited, PDF included
            </p>

            <div style={{
              background: T.blue50,
              border: `1px solid ${T.blue100}`,
              borderRadius: T.radiusSm,
              padding: '1.25rem',
              marginBottom: '1.25rem',
            }}>
              <div style={{ fontSize: mobile ? '2.2rem' : '2.6rem', fontWeight: 800, color: T.gray900, lineHeight: 1 }}>
                ${amount}<span style={{ fontSize: '1.1rem', fontWeight: 700, color: T.gray500 }}>/year</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: isMember ? '#15803d' : T.gray500, marginTop: '0.45rem', fontWeight: 600 }}>
                {isMember
                  ? `Section 8 Administrators member rate applied (standard $${PRICES.standard}/year)`
                  : `Standard rate. Section 8 Administrators members pay $${PRICES.member}/year`}
              </div>
            </div>

            {payUrl ? (
              <a
                href={payUrl}
                style={{
                  display: 'block',
                  padding: '0.9rem',
                  background: T.blue600,
                  color: T.white,
                  borderRadius: T.radiusPill,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  transition: 'background 0.2s ease',
                }}
                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; }}
                onMouseOut={e => { e.currentTarget.style.background = T.blue600; }}
              >
                Proceed to Secure Checkout · ${amount}/year
              </a>
            ) : (
              <div style={{
                background: T.gray100,
                border: `1px solid ${T.gray200}`,
                borderRadius: T.radiusSm,
                padding: '0.9rem 1rem',
                fontSize: '0.85rem',
                color: T.gray600,
                lineHeight: 1.55,
              }}>
                Online checkout is being finalized. We&rsquo;ll follow up right away to
                complete your <b>${amount}</b> payment and start your schedule.
              </div>
            )}

            {/* Alternative payment: checks, purchase orders, etc. */}
            <div style={{
              marginTop: '1.25rem',
              paddingTop: '1.1rem',
              borderTop: `1px solid ${T.gray100}`,
              textAlign: 'left',
            }}>
              {altSent ? (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '12px',
                  padding: '1rem 1.1rem',
                  fontSize: '0.88rem',
                  color: '#166534',
                  textAlign: 'center',
                  lineHeight: 1.55,
                }}>
                  <b>Got it!</b> We saved your preferred payment method and will reach out
                  shortly about how to pay. Your schedule request is in.
                </div>
              ) : !showAlt ? (
                <p style={{ margin: 0, fontSize: '0.82rem', color: T.gray600, textAlign: 'center', lineHeight: 1.55 }}>
                  Can&rsquo;t pay by credit card or the options offered at checkout?{' '}
                  <button
                    onClick={() => setShowAlt(true)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700,
                      color: T.blue600, textDecoration: 'underline',
                    }}
                  >
                    Click here to pay another way
                  </button>
                  {' '}(check, purchase order, etc.)
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: T.gray800, fontWeight: 600 }}>
                    Tell us how you&rsquo;d prefer to pay and we&rsquo;ll reach out shortly to arrange it:
                  </p>
                  <input
                    type="text"
                    value={altHa}
                    onChange={e => setAltHa(e.target.value)}
                    placeholder="Housing authority name"
                    style={altInputStyle}
                  />
                  <input
                    type="email"
                    value={altEmail}
                    onChange={e => setAltEmail(e.target.value)}
                    placeholder="Work email"
                    style={altInputStyle}
                  />
                  <textarea
                    value={altMethod}
                    onChange={e => setAltMethod(e.target.value)}
                    rows={3}
                    placeholder="How would you prefer to pay? e.g. check by mail, purchase order, ACH from our finance office…"
                    style={{ ...altInputStyle, resize: 'vertical' }}
                  />
                  {altError && (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#b91c1c' }}>{altError}</p>
                  )}
                  <button
                    onClick={submitAlt}
                    disabled={altSending}
                    style={{
                      padding: '0.75rem',
                      background: T.blue600,
                      color: T.white,
                      border: 'none',
                      borderRadius: '999px',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      cursor: altSending ? 'not-allowed' : 'pointer',
                      opacity: altSending ? 0.7 : 1,
                      fontFamily: T.font,
                    }}
                  >
                    {altSending ? 'Sending…' : 'Submit'}
                  </button>
                </div>
              )}
            </div>

            <p style={{ fontSize: '0.75rem', color: T.gray400, margin: '1rem 0 0', lineHeight: 1.5 }}>
              Questions before paying? Email{' '}
              <a href="mailto:contact@gethousingai.com" style={{ color: T.blue600 }}>contact@gethousingai.com</a>.
            </p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={() => navigate('/utility-allowances')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: T.font,
                fontSize: '0.85rem',
                color: T.gray500,
                textDecoration: 'underline',
              }}
            >
              Back to schedules
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0', fontFamily: T.font }}>
        <div style={{
          maxWidth: '1200px', margin: '0 auto', padding: '0 1rem',
          display: 'flex', flexDirection: mobile ? 'column' : 'row',
          flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center',
          gap: mobile ? '0.5rem' : '1rem',
        }}>
          <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: mobile ? '0.78rem' : '0.875rem', color: '#d1d5db' }}>
            © 2026 HousingAI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}